/**
 * Muzzle.js — the hero effect.
 * owner: fx
 *
 * Four layers, all seeded from the shot's RNG so consecutive rounds never look
 * identical:
 *   0  white-hot core with an anisotropic star and 3–6 irregular petals
 *   1  hot gas plume projected forward down the bore, turbulent and expanding
 *   2  a second, weaker off-axis flash a fraction later (real flashes flicker)
 *   +  smoke wisps and embers, injected into the world particle pools
 *   +  a real point light for ~40 ms (see Lights.js)
 *
 * One instanced draw call per scene. An instance is placed in ctx.viewScene when
 * a first-person viewmodel exists so it composites correctly with the weapon,
 * and in ctx.scene otherwise (or additionally, for third-person / AI fire).
 */
import * as THREE from 'three';
import { NOISE, DEPTH_HELPERS } from './Shaders.js';

const FLASH_VS = /* glsl */`
precision highp float;
attribute vec4 iPos;      // xyz centre, w size
attribute vec4 iDir;      // xyz barrel forward, w seed
attribute vec4 iParams;   // t, layer, intensity, roll

varying vec2 vUv;
varying float vT, vLayer, vIntensity, vSeed, vRoll;
varying float vViewZ;

void main(){
  if (iParams.z <= 0.0 || iParams.x >= 1.0){ gl_Position = vec4(0.0, 0.0, -3.0, 1.0); return; }
  float layer = iParams.y;
  float t = iParams.x;

  vec3 fwd = normalize(iDir.xyz);
  float push = (layer < 0.5) ? 0.0 : ((layer < 1.5) ? 0.09 + t * 0.34 : 0.03);
  vec3 centre = iPos.xyz + fwd * push * iPos.w * 3.2;

  vec4 mv = viewMatrix * vec4(centre, 1.0);
  vec3 right = vec3(1.0, 0.0, 0.0);
  vec3 up = vec3(0.0, 1.0, 0.0);
  vec2 corner = position.xy;

  float size = iPos.w;
  if (layer < 0.5){
    size *= mix(0.55, 1.25, smoothstep(0.0, 0.30, t)) * mix(1.0, 0.55, smoothstep(0.35, 1.0, t));
    float c = cos(iParams.w), s = sin(iParams.w);
    corner = mat2(c, -s, s, c) * corner;
  } else if (layer < 1.5){
    // gas plume: stretch along the projected barrel axis
    vec3 fv = (viewMatrix * vec4(fwd, 0.0)).xyz;
    float l = length(fv.xy);
    if (l > 0.02){
      vec2 d = fv.xy / l;
      up = vec3(d, 0.0); right = vec3(d.y, -d.x, 0.0);
    }
    size *= mix(0.5, 2.1, t);
    corner.y = corner.y * 1.9 + 0.55;
    corner.x *= 1.0 - t * 0.25;
  } else {
    size *= mix(0.35, 0.95, smoothstep(0.0, 0.5, t)) * mix(1.0, 0.4, smoothstep(0.4, 1.0, t));
    float c = cos(iParams.w * 2.3), s = sin(iParams.w * 2.3);
    corner = mat2(c, -s, s, c) * corner;
  }

  vec3 vpos = mv.xyz + right * corner.x * size + up * corner.y * size;
  gl_Position = projectionMatrix * vec4(vpos, 1.0);
  vUv = uv; vT = t; vLayer = layer; vIntensity = iParams.z; vSeed = iDir.w; vRoll = iParams.w;
  vViewZ = -vpos.z;
}
`;

const FLASH_FS = /* glsl */`
precision highp float;
${NOISE}
${DEPTH_HELPERS}
uniform vec2 uResolution;
uniform float uHasDepth;

varying vec2 vUv;
varying float vT, vLayer, vIntensity, vSeed, vRoll;
varying float vViewZ;

void main(){
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float ang = atan(p.y, p.x);
  float t = vT;
  vec3 col = vec3(0.0);
  float a = 0.0;

  if (vLayer < 0.5){
    /* ---- core + star + petals ---- */
    float lobes = 3.0 + floor(hash11(vSeed * 3.1) * 4.0);
    float phase = hash11(vSeed * 7.7) * 6.2832;
    float petal = pow(abs(cos(ang * lobes * 0.5 + phase)), 0.55);
    float jag = fbm2(vec2(cos(ang), sin(ang)) * 3.2 + vSeed * 11.0, 4, 0.55);
    float radius = mix(0.30, 0.98, petal) * mix(0.72, 1.12, jag);
    float body = smoothstep(radius, radius * 0.10, r);
    float core = exp(-r * r * 34.0);
    // anisotropic star: one long horizontal blade + a shorter vertical one
    float bl = 0.0;
    vec2 q = rot2(-vRoll) * p;
    bl += pow(clamp(1.0 - abs(q.x), 0.0, 1.0), 3.0) * exp(-abs(q.y) * 55.0);
    bl += pow(clamp(1.0 - abs(q.y) * 1.7, 0.0, 1.0), 3.0) * exp(-abs(q.x) * 70.0) * 0.55;
    bl += pow(clamp(1.0 - length(rot2(0.7854) * q * vec2(1.0, 6.0)), 0.0, 1.0), 3.0) * 0.35;

    float e = mix(0.35, 1.0, smoothstep(0.0, 0.12, t)) * (1.0 - smoothstep(0.30, 1.0, t));
    float flick = 0.75 + 0.25 * hash11(vSeed * 13.0 + floor(t * 6.0));
    a = clamp(body * 0.9 + core * 1.1 + bl * 0.75, 0.0, 1.35) * e * flick;
    // the hot core is small: most of the flash area must stay orange, or the
    // whole thing clips to a white disc
    vec3 hot   = vec3(3.2, 2.5, 1.7);
    vec3 mid   = vec3(3.0, 1.05, 0.22);
    vec3 rim   = vec3(2.2, 0.36, 0.035);
    col = mix(rim, mid, smoothstep(0.95, 0.30, r));
    col = mix(col, hot, clamp(core * 1.25 + bl * 0.55, 0.0, 1.0));
    col *= 7.5 * vIntensity;
  } else if (vLayer < 1.5){
    /* ---- forward gas plume ---- */
    float y = clamp(vUv.y, 0.0, 1.0);
    float widen = mix(0.28, 1.0, pow(y, 0.65));
    float x = abs(p.x) / max(widen, 1e-3);
    float turb = fbm2(vec2(p.x * 2.4, y * 3.4 - t * 1.4) + vSeed * 5.0, 5, 0.55);
    float body = smoothstep(1.0, 0.1, x) * smoothstep(1.0, 0.75, y) * smoothstep(0.0, 0.15, y);
    body *= mix(0.45, 1.25, turb);
    float e = smoothstep(0.0, 0.10, t) * (1.0 - smoothstep(0.25, 1.0, t));
    a = clamp(body, 0.0, 1.0) * e;
    col = mix(vec3(2.4, 0.52, 0.07), vec3(3.0, 1.85, 0.75), smoothstep(0.55, 0.0, y + x * 0.3));
    col *= 4.6 * vIntensity;
  } else {
    /* ---- secondary off-axis flare ---- */
    float lobes = 2.0 + floor(hash11(vSeed * 17.0) * 3.0);
    float petal = pow(abs(cos(ang * lobes * 0.5 + vSeed * 5.0)), 0.8);
    float radius = mix(0.25, 0.9, petal);
    float body = smoothstep(radius, radius * 0.12, r);
    float core = exp(-r * r * 26.0);
    float e = smoothstep(0.10, 0.28, t) * (1.0 - smoothstep(0.40, 1.0, t));
    a = clamp(body * 0.7 + core * 0.85, 0.0, 1.25) * e;
    col = mix(vec3(2.2, 0.55, 0.09), vec3(3.0, 2.1, 1.15), core) * 4.8 * vIntensity;
  }

  if (a <= 0.004) discard;

  if (uHasDepth > 0.5){
    float sceneZ = fxSceneZ(gl_FragCoord.xy / uResolution);
    a *= clamp((sceneZ - vViewZ) / 0.25, 0.0, 1.0);
  }

  vec4 outColor = vec4(col, a);
  #if defined( TONE_MAPPING )
    outColor.rgb = toneMapping(outColor.rgb);
  #endif
  outColor = linearToOutputTexel(outColor);
  gl_FragColor = outColor;
}
`;

const LAYERS = 3;

export class MuzzleFlash {
  constructor(capacity = 8) {
    this.capacity = capacity;
    const n = capacity * LAYERS;
    this.n = n;
    this.head = 0;

    this.iPos = new Float32Array(n * 4);
    this.iDir = new Float32Array(n * 4);
    this.iParams = new Float32Array(n * 4);
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.live = new Uint8Array(capacity);
    this.hold = new Float32Array(capacity);   // >0 pins normalised age (shot presets)

    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0,
    ], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this.aPos = new THREE.InstancedBufferAttribute(this.iPos, 4).setUsage(THREE.DynamicDrawUsage);
    this.aDir = new THREE.InstancedBufferAttribute(this.iDir, 4).setUsage(THREE.DynamicDrawUsage);
    this.aParams = new THREE.InstancedBufferAttribute(this.iParams, 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iPos', this.aPos);
    g.setAttribute('iDir', this.aDir);
    g.setAttribute('iParams', this.aParams);
    g.instanceCount = n;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.uniforms = {
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uHasDepth: { value: 0 },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: FLASH_VS,
      fragmentShader: FLASH_FS,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3100;
    this.mesh.name = 'fx_muzzleflash';
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
    this.geometry = g;
  }

  /**
   * @param {THREE.Vector3} pos world muzzle position
   * @param {THREE.Vector3} dir barrel forward
   * @param {object} o { size, intensity, life, seed, hold }
   */
  fire(pos, dir, o = {}) {
    const slot = this.head;
    this.head = (this.head + 1) % this.capacity;
    const seed = o.seed ?? 0.5;
    const size = o.size ?? 0.34;
    const intensity = o.intensity ?? 1;
    this.age[slot] = 0;
    this.life[slot] = o.life ?? 0.052;
    this.live[slot] = 1;
    this.hold[slot] = o.hold ?? 0;
    const roll = seed * Math.PI * 2;
    for (let l = 0; l < LAYERS; l++) {
      const i = (slot * LAYERS + l) * 4;
      this.iPos[i] = pos.x; this.iPos[i + 1] = pos.y; this.iPos[i + 2] = pos.z;
      this.iPos[i + 3] = size * (l === 1 ? 0.85 : (l === 2 ? 0.75 : 1.0));
      this.iDir[i] = dir.x; this.iDir[i + 1] = dir.y; this.iDir[i + 2] = dir.z;
      this.iDir[i + 3] = seed * 97.13 + l * 3.7;
      this.iParams[i] = 0;
      this.iParams[i + 1] = l;
      this.iParams[i + 2] = intensity * (l === 2 ? 0.55 : 1.0);
      this.iParams[i + 3] = roll + (l === 2 ? 1.1 : 0);
    }
    this._dirty = true;
    return slot;
  }

  update(dt, env) {
    let any = false;
    for (let s = 0; s < this.capacity; s++) {
      if (!this.live[s]) continue;
      any = true;
      let t;
      if (this.hold[s] > 0) {
        t = this.hold[s];
      } else {
        this.age[s] += dt;
        t = this.age[s] / this.life[s];
        if (t >= 1) { this.live[s] = 0; t = 1; }
      }
      for (let l = 0; l < LAYERS; l++) this.iParams[(s * LAYERS + l) * 4] = t;
      this._dirty = true;
    }
    if (this._dirty) {
      this.aPos.needsUpdate = true;
      this.aDir.needsUpdate = true;
      this.aParams.needsUpdate = true;
      this._dirty = false;
    }
    this.uniforms.tDepth.value = env.depthTexture;
    this.uniforms.tNormal.value = env.normalTexture || env.depthTexture;
    this.uniforms.uDepthMode.value = env.depthMode;
    this.uniforms.uHasDepth.value = env.depthTexture && !env.viewSpace ? 1 : 0;
    this.uniforms.uResolution.value.copy(env.resolution);
    this.mesh.visible = any;
  }

  reset() {
    this.live.fill(0);
    this.hold.fill(0);
    this.iParams.fill(0);
    for (let i = 0; i < this.n; i++) this.iParams[i * 4] = 1;
    this._dirty = true;
    this.head = 0;
  }

  dispose() { this.geometry.dispose(); this.mesh.material.dispose(); }
}
